import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";

import getAPIClient from "../hooks/apiClient";
import Header from "../elements/Header";
import Loading from "../elements/Loading";
import InlineNotification from "../elements/InlineNotification";
import OneSelectUI from "../elements/inputs/one-select";
import SubmitButton from "../elements/inputs/SubmitButton";
import Popup from "../elements/popup";
import VacancySettingsPage from "./VacancySettingsPage";
import s from "../styles/pages/resume_batch.module.scss";

const STORAGE_KEY_PREFIX = 'resume-selection-stage-filter-';

function safeNumber(value) {
    if (value === undefined || value === null || value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
}

function parsePlacementOptions(raw) {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (_) {
        return null;
    }
}

function getStatusMeta(status) {
    if (status === 'created') return {label: 'Уже оценено', className: s.statusCreated};
    if (status === 'creating') return {label: 'Оценивается', className: s.statusProcessing};
    if (status === 'updating') return {label: 'Переоценивается', className: s.statusProcessing};
    if (status === 'error') return {label: 'Ошибка оценки', className: s.statusError};
    return {label: 'Без оценки', className: s.statusPending};
}

function getScoreMeta(score) {
    if (score === undefined || score === null) {
        return {
            value: '--',
            color: '#CBD5E1',
            progressDeg: 0,
        };
    }

    const normalized = Math.max(1, Math.min(10, Number(score)));
    let color = '#EF4444';
    if (normalized >= 8) color = '#22C55E';
    else if (normalized >= 6) color = '#F59E0B';

    return {
        value: normalized,
        color,
        progressDeg: normalized * 36,
    };
}

function toSortableScore(score) {
    if (score === undefined || score === null || score === '') return null;
    const parsed = Number(score);
    if (!Number.isFinite(parsed)) return null;
    return Math.max(1, Math.min(10, parsed));
}

async function resolveVacancyId(BX24, searchParams) {
    const fromQuery = safeNumber(searchParams.get('vacancy_id'));
    if (fromQuery) return fromQuery;

    try {
        const placement = await BX24.placement.info();
        const rawFromPlacement = placement?.options?.ID || placement?.options?.ENTITY_ID || placement?.options?.entityId;
        const normalized = safeNumber(rawFromPlacement);
        if (normalized) return normalized;
    } catch (_) {
    }

    const allParams = BX24?.getAllParams?.() || {};
    const direct = safeNumber(allParams.ID || allParams.ENTITY_ID || allParams.entityId);
    if (direct) return direct;

    const parsed = parsePlacementOptions(allParams.PLACEMENT_OPTIONS || allParams.placement_options);
    const fromParsed = safeNumber(parsed?.ID || parsed?.ENTITY_ID || parsed?.entityId);
    if (fromParsed) return fromParsed;

    return null;
}

export default function ResumeSelectionPage({BX24}) {
    const api = useMemo(() => getAPIClient(BX24), []);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [isLoading, setIsLoading] = useState(true);
    const [notificationText, setNotificationText] = useState('');
    const [notificationType, setNotificationType] = useState('error');

    const [vacancyId, setVacancyId] = useState(null);
    const [directions, setDirections] = useState([]);
    const [allStagesByDirection, setAllStagesByDirection] = useState({});

    const [selectedDirection, setSelectedDirection] = useState(null);
    const [selectedStages, setSelectedStages] = useState([]);
    const [filtersInitialized, setFiltersInitialized] = useState(false);

    const [resumes, setResumes] = useState([]);
    const [selectedResumeIds, setSelectedResumeIds] = useState(new Set());
    const [forceReanalyze, setForceReanalyze] = useState(false);
    const [portalSettings, setPortalSettings] = useState(null);

    const [vacancyPopupOpened, setVacancyPopupOpened] = useState(false);

    const handleError = useCallback((defaultText, e) => {
        console.error(defaultText, e);
        setNotificationType('error');
        setNotificationText(e?.response?.data?.detail || defaultText);
    }, []);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            try {
                BX24.fitWindow();
                const id = await resolveVacancyId(BX24, searchParams);
                if (!id) {
                    setNotificationType('error');
                    setNotificationText('Не удалось получить ID вакансии из контекста карточки');
                    setIsLoading(false);
                    return;
                }
                setVacancyId(id);
            } catch (e) {
                handleError('Ошибка инициализации экрана отбора резюме', e);
            } finally {
                setIsLoading(false);
            }
        }
        init();
    }, [BX24, searchParams, handleError]);

    useEffect(() => {
        const loadMeta = async () => {
            if (!vacancyId) return;
            setFiltersInitialized(false);
            setIsLoading(true);
            try {
                const directionsResponse = await api.get('/analysis_agent/applicant/directions');
                const directionOptions = (directionsResponse.data || []).map((d) => ({
                    ...d,
                    value: d.direction_id,
                    label: d.direction_name,
                }));
                setDirections(directionOptions);

                const stagesMap = {};
                for (const direction of directionOptions) {
                    const params = {};
                    if (direction.direction_id !== 0) params.direction_id = direction.direction_id;
                    const stagesResponse = await api.get('/analysis_agent/applicant/stages', {params});
                    stagesMap[direction.direction_id] = (stagesResponse.data || []).map((stage) => ({
                        ...stage,
                        direction_id: direction.direction_id,
                        value: stage.stage_id,
                        label: `${stage.stage_name}${stage.is_final ? ' (финальный)' : ''}`,
                    }));
                }
                setAllStagesByDirection(stagesMap);
                const settingsResponse = await api.get('/bitrix_portals/settings');
                setPortalSettings(settingsResponse.data || null);

                const storageKey = `${STORAGE_KEY_PREFIX}${vacancyId}`;
                const persistedRaw = localStorage.getItem(storageKey);
                if (persistedRaw) {
                    let persistedDirectionId = null;
                    let persistedStageIds = [];
                    try {
                        const parsed = JSON.parse(persistedRaw);
                        if (Array.isArray(parsed)) {
                            persistedStageIds = parsed.map((id) => String(id));
                        } else if (parsed && typeof parsed === 'object') {
                            if (Array.isArray(parsed.direction_ids) && parsed.direction_ids.length > 0) {
                                persistedDirectionId = safeNumber(parsed.direction_ids[0]);
                            } else {
                                persistedDirectionId = safeNumber(parsed.direction_id);
                            }
                            if (Array.isArray(parsed.stage_ids)) {
                                persistedStageIds = parsed.stage_ids.map((id) => String(id));
                            }
                        }
                    } catch (_) {
                        persistedStageIds = [String(persistedRaw)];
                    }

                    const allStages = Object.values(stagesMap).flat();
                    const persistedStages = allStages.filter((stage) => persistedStageIds.includes(String(stage.stage_id)));

                    if (persistedStages.length) {
                        setSelectedStages(persistedStages);
                    }

                    if (persistedDirectionId !== null) {
                        const direction = directionOptions.find((item) => Number(item.direction_id) === persistedDirectionId);
                        if (direction) setSelectedDirection(direction);
                    } else if (persistedStages.length) {
                        const singleDirectionId = persistedStages[0]?.direction_id;
                        const oneDirection = persistedStages.every((stage) => String(stage.direction_id) === String(singleDirectionId));
                        if (oneDirection) {
                            const direction = directionOptions.find((item) => String(item.direction_id) === String(singleDirectionId));
                            if (direction) setSelectedDirection(direction);
                        }
                    }
                }
            } catch (e) {
                handleError('Ошибка загрузки направлений/этапов', e);
            } finally {
                setFiltersInitialized(true);
                setIsLoading(false);
            }
        }
        loadMeta();
    }, [api, vacancyId, handleError]);

    const visibleStages = useMemo(() => {
        if (selectedDirection) return allStagesByDirection[selectedDirection.direction_id] || [];
        return Object.values(allStagesByDirection).flat();
    }, [selectedDirection, allStagesByDirection]);

    const nonFinalStageIds = useMemo(() => {
        return new Set(visibleStages.filter((stage) => !stage.is_final).map((stage) => stage.stage_id));
    }, [visibleStages]);

    const selectedStageIds = useMemo(() => {
        return new Set((selectedStages || []).map((stage) => stage.stage_id));
    }, [selectedStages]);

    const sortedResumes = useMemo(() => {
        return [...resumes].sort((a, b) => {
            const scoreA = toSortableScore(a.ai_score);
            const scoreB = toSortableScore(b.ai_score);

            if (scoreA === null && scoreB === null) return a.crm_item_id - b.crm_item_id;
            if (scoreA === null) return 1;
            if (scoreB === null) return -1;
            if (scoreB !== scoreA) return scoreB - scoreA;
            return a.crm_item_id - b.crm_item_id;
        });
    }, [resumes]);

    const loadResumes = useCallback(async () => {
        if (!vacancyId) return;
        setIsLoading(true);
        try {
            const params = {vacancy_b24_element_id: vacancyId};
            if (selectedDirection) params.direction_id = selectedDirection.direction_id;
            if (selectedStages.length === 1) params.stage_id = selectedStages[0].stage_id;

            const resumesResponse = await api.get('/analysis_agent/applicant/resumes', {params});
            let items = resumesResponse.data || [];

            if (selectedStages.length > 1) {
                items = items.filter((item) => selectedStageIds.has(item.stage_id));
            } else if (selectedStages.length === 0) {
                items = items.filter((item) => nonFinalStageIds.has(item.stage_id));
            }

            setResumes(items);
            setSelectedResumeIds(new Set(items.map((item) => item.crm_item_id)));
            BX24.fitWindow();
        } catch (e) {
            handleError('Ошибка загрузки резюме', e);
        } finally {
            setIsLoading(false);
        }
    }, [api, vacancyId, selectedDirection, selectedStages, selectedStageIds, nonFinalStageIds, handleError, BX24]);

    useEffect(() => {
        if (!vacancyId || Object.keys(allStagesByDirection).length === 0) return;
        loadResumes();
    }, [vacancyId, allStagesByDirection, selectedDirection, selectedStages, loadResumes]);

    const toggleResume = (resumeId) => {
        setSelectedResumeIds((prev) => {
            const next = new Set(prev);
            if (next.has(resumeId)) next.delete(resumeId);
            else next.add(resumeId);
            return next;
        });
    };

    const toggleAll = () => {
        setSelectedResumeIds((prev) => {
            if (prev.size === resumes.length) return new Set();
            return new Set(resumes.map((item) => item.crm_item_id));
        });
    };

    const persistStageFilter = (stageOptions) => {
        const normalized = Array.isArray(stageOptions) ? stageOptions : (stageOptions ? [stageOptions] : []);
        setSelectedStages(normalized);
    };

    useEffect(() => {
        if (!vacancyId || !filtersInitialized) return;
        const storageKey = `${STORAGE_KEY_PREFIX}${vacancyId}`;
        if (!selectedDirection && selectedStages.length === 0) {
            localStorage.removeItem(storageKey);
            return;
        }

        localStorage.setItem(storageKey, JSON.stringify({
            direction_id: selectedDirection?.direction_id ?? null,
            stage_ids: selectedStages.map((stage) => stage.stage_id),
        }));
    }, [vacancyId, selectedDirection, selectedStages, filtersInitialized]);

    const getResumePath = useCallback((crmItemId) => {
        if (!portalSettings) return null;
        const type = portalSettings.smart_process_type;
        if (type === 'lead') return `/crm/lead/details/${crmItemId}/`;
        if (type === 'deal') return `/crm/deal/details/${crmItemId}/`;
        if (type === 'smart_process' && portalSettings.smart_process_id) {
            return `/crm/type/${portalSettings.smart_process_id}/details/${crmItemId}/`;
        }
        return null;
    }, [portalSettings]);

    const openResumeCard = useCallback((crmItemId) => {
        const path = getResumePath(crmItemId);
        if (!path) {
            setNotificationType('error');
            setNotificationText('Не удалось построить ссылку на карточку резюме. Проверьте настройки смарт-процесса.');
            return;
        }
        if (typeof BX24?.openPath === 'function') {
            BX24.openPath(path);
            return;
        }
        window.open(path, '_blank', 'noopener,noreferrer');
    }, [BX24, getResumePath]);

    const startBatchEvaluate = async () => {
        const ids = Array.from(selectedResumeIds);
        if (ids.length === 0) {
            setNotificationType('error');
            setNotificationText('Выберите хотя бы одно резюме');
            return;
        }

        setIsLoading(true);
        try {
            await api.post('/analysis_agent/applicant/evaluate/batch', {
                crm_item_ids: ids,
                vacancy_b24_element_id: vacancyId,
                force_reanalyze: forceReanalyze,
            });
            const directionParam = selectedDirection ? `&direction_id=${selectedDirection.direction_id}` : '';
            navigate(`/resume_dashboard?vacancy_id=${vacancyId}&ids=${ids.join(',')}${directionParam}`);
        } catch (e) {
            handleError('Ошибка запуска пакетной оценки', e);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={s.page}>
            <Loading isLoading={isLoading}/>
            <Header/>
            <div className={s.container}>
                <h1>Выбор резюме для AI-оценки</h1>
                <InlineNotification
                    type={notificationType}
                    text={notificationText}
                    hideFlag={!notificationText}
                />

                <div className={`${s.scoreTools} ${s.compactSelectRow}`}>
                    <OneSelectUI
                        title={'Направление'}
                        selectionOptions={directions}
                        selectedOption={selectedDirection}
                        setSelectedOption={(value) => {
                            setSelectedDirection(value || null);
                            setSelectedStages([]);
                        }}
                        isClearable={true}
                        placeholder={'Все направления'}
                    />
                    <OneSelectUI
                        title={'Этап'}
                        selectionOptions={visibleStages}
                        selectedOption={selectedStages}
                        setSelectedOption={persistStageFilter}
                        isMulti={true}
                        closeMenuOnSelect={false}
                        isClearable={true}
                        placeholder={'Все нефинальные этапы'}
                    />
                </div>

                <div className={s.actionsButtons}>
                    <label className={s.secondaryButton} style={{display: 'inline-flex', gap: '8px', alignItems: 'center'}}>
                        <input
                            type={'checkbox'}
                            checked={forceReanalyze}
                            onChange={(event) => setForceReanalyze(event.target.checked)}
                        />
                        Разрешить переоценку уже оцененных
                    </label>
                    <button className={s.secondaryButton} onClick={() => setVacancyPopupOpened(true)}>
                        Изменить описание вакансии
                    </button>
                    <button className={s.secondaryButton} onClick={toggleAll}>
                        {selectedResumeIds.size === resumes.length ? 'Снять выбор' : 'Выбрать все'}
                    </button>
                    <div className={s.selectionCounter}>
                        Выбрано {selectedResumeIds.size} из {resumes.length}
                    </div>
                    <SubmitButton
                        text={'Перейти к дашборду'}
                        onClick={startBatchEvaluate}
                        disabled={selectedResumeIds.size === 0}
                    />
                </div>

                <div className={s.resumeList}>
                    {sortedResumes.map((resume) => {
                        const statusMeta = getStatusMeta(resume.ai_status);
                        const scoreMeta = getScoreMeta(resume.ai_score);

                        return (
                            <div key={resume.crm_item_id} className={s.resumeRow}>
                                <div className={s.resumeMain}>
                                    <input
                                        type={'checkbox'}
                                        checked={selectedResumeIds.has(resume.crm_item_id)}
                                        onChange={() => toggleResume(resume.crm_item_id)}
                                    />
                                    <div
                                        className={s.scoreCircleSmall}
                                        style={{
                                            background: `conic-gradient(${scoreMeta.color} ${scoreMeta.progressDeg}deg, #E2E8F0 0deg)`,
                                        }}
                                    >
                                        <div className={s.scoreCircleSmallInner}>{scoreMeta.value}</div>
                                    </div>

                                    <div className={s.resumeInfo}>
                                        <div className={s.cardTitle}>{resume.title || `Резюме #${resume.crm_item_id}`}</div>
                                        <div className={s.cardMeta}>ID: {resume.crm_item_id}</div>
                                    </div>
                                </div>

                                <div className={s.resumeActions}>
                                    <span className={`${s.statusBadge} ${statusMeta.className}`}>{statusMeta.label}</span>
                                    <button
                                        type={'button'}
                                        className={s.secondaryButton}
                                        onClick={() => openResumeCard(resume.crm_item_id)}
                                    >
                                        Перейти к резюме
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {resumes.length === 0 && (
                    <div className={s.empty}>По текущему фильтру резюме не найдено</div>
                )}
            </div>

            <Popup openedFlag={vacancyPopupOpened} setOpenedFlag={setVacancyPopupOpened}>
                {vacancyId && <VacancySettingsPage BX24={BX24} vacancyItemId={vacancyId}/>} 
            </Popup>
        </div>
    );
}
